import { CartUpdateEvent } from '@theme/events';

const STORAGE_KEY = 'gbWishlist:v1';
const MAX_ITEMS = 20;
const HANDLE = /^[a-z0-9-]+$/i;

function readItems() {
  const items = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  if (!Array.isArray(items) || items.length > MAX_ITEMS || items.some((item) =>
    !item || typeof item.id !== 'string' || !/^\d+$/.test(item.id) || typeof item.handle !== 'string' || !HANDLE.test(item.handle)
  )) {
    throw new Error('error');
  }
  return items;
}

function writeItems(items) {
  if (items.length > MAX_ITEMS) throw new Error('full');
  const value = JSON.stringify(items);
  localStorage.setItem(STORAGE_KEY, value);
  if (localStorage.getItem(STORAGE_KEY) !== value) throw new Error('error');
}

class Wishlist extends HTMLElement {
  #listeners;
  #busy = false;

  connectedCallback() {
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#strings = JSON.parse(this.querySelector('[data-wish-strings]').textContent);
    document.addEventListener('click', this.#onClick, { signal });
    window.addEventListener('storage', () => this.#sync(), { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
  }

  #strings;

  #onClick = (event) => {
    const toggle = event.target.closest('[data-gb-wish-toggle]');
    const add = event.target.closest('[data-wish-add]');
    const remove = event.target.closest('[data-wish-remove]');
    const open = event.target.closest('[data-wish-open]');
    const close = event.target.closest('[data-wish-close]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      this.#toggle(toggle.getAttribute('data-variant-id'), toggle.getAttribute('data-handle'));
    } else if (add) {
      event.preventDefault();
      this.#addToCart(add.getAttribute('data-variant-id'));
    } else if (remove) {
      event.preventDefault();
      this.#remove(remove.getAttribute('data-variant-id'));
    } else if (open) {
      event.preventDefault();
      this.querySelector('dialog')?.showModal();
      this.#render();
    } else if (close) {
      this.querySelector('dialog')?.close();
    }
  };

  #sync() {
    let items = [];
    try { items = readItems(); } catch { this.#message('error'); }
    const ids = new Set(items.map((item) => item.id));
    document.querySelectorAll('[data-gb-wish-toggle]').forEach((button) => {
      const saved = ids.has(button.getAttribute('data-variant-id'));
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    });
    const count = this.querySelector('[data-wish-count]');
    if (count) {
      count.textContent = String(items.length);
      count.hidden = items.length === 0;
    }
  }

  #toggle(id, handle) {
    if (!id || !HANDLE.test(handle || '')) return;
    try {
      const items = readItems();
      const exists = items.some((item) => item.id === id);
      writeItems(exists ? items.filter((item) => item.id !== id) : [...items, { id, handle }]);
      this.#message('');
    } catch (error) {
      this.#message(error.message === 'full' ? 'full' : 'error');
      this.querySelector('dialog')?.showModal();
    }
    this.#sync();
    this.#render();
  }

  #remove(id) {
    try {
      writeItems(readItems().filter((item) => item.id !== id));
      this.#message('');
    } catch {
      this.#message('error');
    }
    this.#sync();
    this.#render();
  }

  async #addToCart(id) {
    if (this.#busy || !id) return;
    this.#busy = true;
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((node) => node.dataset.sectionId)
      .filter(Boolean)
      .slice(0, 5);
    try {
      const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error('error');
      const cart = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((cartResponse) => cartResponse.json());
      this.dispatchEvent(new CartUpdateEvent({ ...cart, sections: data.sections }, 'gb-wishlist', {
        source: 'gb-wishlist',
        variantId: id,
        itemCount: cart.item_count,
        sections: data.sections,
      }));
      this.#message('added');
    } catch {
      this.#message('error');
    } finally {
      this.#busy = false;
    }
  }

  async #render() {
    const list = this.querySelector('[data-wish-list]');
    if (!list) return;
    let items = [];
    try { items = readItems(); } catch { return; }
    list.replaceChildren();
    if (items.length === 0) {
      const empty = document.createElement('li');
      empty.textContent = this.#strings.empty;
      list.append(empty);
      return;
    }
    const rows = items.map(() => {
      const row = document.createElement('li');
      row.className = 'gb-wishlist__item';
      list.append(row);
      return row;
    });
    await Promise.all(items.map(async (item, index) => {
      const row = rows[index];
      try {
        const root = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
        const product = await fetch(`${root}products/${item.handle}.js`, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error('error');
          return response.json();
        });
        const variant = product.variants.find((entry) => String(entry.id) === item.id) || product.variants[0];
        const rawImage = variant.featured_image?.src || variant.featured_image || product.featured_image;
        const image = typeof rawImage === 'string' ? rawImage : rawImage?.src;
        if (image) {
          const img = document.createElement('img');
          img.src = image;
          img.alt = '';
          img.width = 72;
          img.height = 90;
          row.append(img);
        }
        const details = document.createElement('div');
        const link = document.createElement('a');
        link.href = `${root}products/${item.handle}?variant=${item.id}`;
        link.textContent = product.title;
        details.append(link);
        const price = document.createElement('p');
        price.textContent = this.#money(variant.price);
        details.append(price);
        const actions = document.createElement('div');
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'button';
        add.dataset.wishAdd = '';
        add.dataset.variantId = item.id;
        add.textContent = variant.available ? this.#strings.add : this.#strings.unavailable;
        add.disabled = !variant.available;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.dataset.wishRemove = '';
        remove.dataset.variantId = item.id;
        remove.textContent = this.#strings.remove;
        actions.append(add, remove);
        details.append(actions);
        row.append(details);
      } catch {
        row.textContent = this.#strings.error;
      }
    }));
  }

  #money(cents) {
    return `Rs.${(cents / 100).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  #message(key) {
    const status = this.querySelector('[data-wish-status]');
    if (status) status.textContent = key ? this.#strings[key] || '' : '';
  }
}

if (!customElements.get('gb-wishlist')) customElements.define('gb-wishlist', Wishlist);
